import { normalizeCampaignArtEntry } from '../../../shared/campaign/utils/artDisplayCacheBust';

const hasFilledAnswer = (answer) => {
  if (Array.isArray(answer)) {
    return answer.length > 0 && answer[0] !== '';
  }

  return answer != null && String(answer).trim() !== '';
};

const PRE_STRATEGY_STATUSES = [
  'in_progress',
  'questions_completed',
  'strategy_processing',
  'strategy_ready',
  'parametrization',
];

const POST_STRATEGY_STATUSES = [
  'strategy_approved',
  'art_generation_in_progress',
  'arts_ready',
  'arts_approved',
  'completed',
];

export const buildHistoryFromCampaignData = (
  questionsData = [],
  options = {},
) => {
  const { strategyResult, isFirstCampaign = false } = options;

  if (!Array.isArray(questionsData)) {
    return [];
  }

  const history = [];

  for (const step of questionsData) {
    if (!step?.messages) {
      continue;
    }

    for (const msg of step.messages) {
      if (!hasFilledAnswer(msg.answer)) {
        continue;
      }

      const isPalette = msg.component === 'ColorPaletteSelector';
      const rawAnswer = Array.isArray(msg.answer) ? msg.answer[0] : msg.answer;
      const answer =
        rawAnswer === 'noAnswer'
          ? ''
          : isPalette && Array.isArray(msg.answer)
            ? msg.answer.join(', ')
            : rawAnswer;

      history.push({
        stepOrder: step.stepOrder,
        stepName: step.stepName,
        messageOrder: msg.messageOrder,
        question: msg.question || msg.message || '',
        answer,
        options: msg.options || [],
        imageUrl: isPalette
          ? []
          : Array.isArray(msg.answer)
            ? msg.answer.slice(1)
            : [],
        component: msg.component,
        isFirstCampaign,
        isUserMessage: msg.isUserMessage === true,
        isAutoFilledFromDatabase: msg.isAutoFilledFromDatabase === true,
      });
    }
  }

  if (Array.isArray(strategyResult?.conversationHistory)) {
    strategyResult.conversationHistory
      .filter((item) => item && typeof item.question === 'string')
      .forEach((item, index) => {
        history.push({
          stepOrder: typeof item.stepOrder === 'number' ? item.stepOrder : 999,
          messageOrder:
            typeof item.messageOrder === 'number' ? item.messageOrder : index,
          question: item.question,
          answer: typeof item.answer === 'string' ? item.answer : undefined,
          options: Array.isArray(item.options) ? item.options : [],
          imageUrl: Array.isArray(item.imageUrl) ? item.imageUrl : [],
          component:
            typeof item.component === 'string' ? item.component : undefined,
        });
      });
  }

  return history;
};

export const findPendingQuestionFromCampaignData = (questionsData = []) => {
  if (!Array.isArray(questionsData)) {
    return null;
  }

  for (const step of questionsData) {
    if (!step?.messages) {
      continue;
    }

    for (const msg of step.messages) {
      if (msg?.component === 'StrategyStepsList' || msg?.isUserMessage) {
        continue;
      }

      if (!hasFilledAnswer(msg.answer) && msg.answer?.[0] !== 'noAnswer') {
        return {
          stepOrder: step.stepOrder,
          stepName: step.stepName,
          messageOrder: msg.messageOrder,
          question: msg.question || msg.message || '',
          options: msg.options || [],
          component: msg.component,
        };
      }
    }
  }

  return null;
};

export const resolveEffectiveCampaignStatus = (campaign = {}) => {
  const status = campaign.status || campaign.campaignStatus || 'in_progress';
  const artsCount = Array.isArray(campaign.arts)
    ? campaign.arts.length
    : Array.isArray(campaign.results)
      ? campaign.results.length
      : 0;

  if (!PRE_STRATEGY_STATUSES.includes(status) || artsCount === 0) {
    return status;
  }

  const history = Array.isArray(campaign.history) ? campaign.history : [];

  const hasSchedulingChoice = history.some(
    (item) =>
      Number(item?.stepOrder) === 1 &&
      Number(item?.messageOrder) === 8 &&
      hasFilledAnswer(item?.answer),
  );

  const hasArtApproval = history.some((item) => {
    const answer = String(item?.answer || '').toLowerCase();
    return answer.includes('aprovar artes') || answer.includes('approve arts');
  });

  if (hasSchedulingChoice) {
    return 'arts_approved';
  }

  if (hasArtApproval || artsCount > 0) {
    return 'arts_ready';
  }

  return status;
};

export const isPostStrategyCampaignStatus = (status) =>
  POST_STRATEGY_STATUSES.includes(status);


const resolvePieceNumber = (art) =>
  Number(art?.piece_number ?? art?.pieceNumber ?? 0);

export const sortArtsByPieceNumber = (arts = []) =>
  [...arts].sort((left, right) => resolvePieceNumber(left) - resolvePieceNumber(right));

export const mergeArtsByPieceNumber = (existing = [], incoming = []) => {
  const byPiece = new Map();

  for (const art of existing) {
    const pieceNumber = resolvePieceNumber(art);
    if (pieceNumber > 0) {
      byPiece.set(pieceNumber, art);
    }
  }

  for (const art of incoming) {
    const pieceNumber = resolvePieceNumber(art);
    if (pieceNumber > 0) {
      byPiece.set(pieceNumber, art);
    }
  }

  return sortArtsByPieceNumber(Array.from(byPiece.values()));
};

export const normalizeCampaignArtsPayload = (campaign = {}) => {
  const rawArts = Array.isArray(campaign.arts) ? campaign.arts : [];
  const rawResults = Array.isArray(campaign.results) ? campaign.results : [];
  const merged =
    rawResults.length > 0
      ? mergeArtsByPieceNumber(rawArts, rawResults)
      : rawArts;
  const arts = sortArtsByPieceNumber(merged).map((art) =>
    normalizeCampaignArtEntry(art),
  );

  return {
    ...campaign,
    arts,
    results: arts,
  };
};

export const deriveSchedulingLockFromHistory = (history = [], status = '') => {
  if (status === 'completed') {
    return { publications: true };
  }

  if (!Array.isArray(history) || history.length === 0) {
    return { publications: false };
  }

  const publicationsLocked = history.some((item) => {
    if (!item) {
      return false;
    }

    if (
      Number(item.stepOrder) === 1 &&
      Number(item.messageOrder) === 8 &&
      hasFilledAnswer(item.answer)
    ) {
      return true;
    }

    if (
      (item.component === 'ScheduleSelector' ||
        item.component === 'SchedulingProgress') &&
      hasFilledAnswer(item.answer)
    ) {
      return true;
    }

    return false;
  });

  return { publications: publicationsLocked };
};

const buildApprovalQuestion = (labels = {}) => ({
  stepOrder: null,
  stepName: null,
  messageOrder: null,
  question: null,
  options: [
    { name: labels.approve, type: 'approve' },
    { name: labels.edit, type: 'edit' },
    { name: labels.reject, type: 'reject' },
  ].filter((option) => typeof option.name === 'string' && option.name.length > 0),
  isApprovalQuestion: true,
});

export const buildArtsReadyApprovalQuestion = (t) =>
  buildApprovalQuestion({
    approve: t('chatsocket.buttons.approve_arts'),
    edit: t('chatsocket.buttons.batch_edit'),
    reject: t('chatsocket.buttons.reject'),
  });

export const buildCompletedCampaignQuestion = (t) => ({
  stepOrder: null,
  stepName: 'campaign_completed_actions',
  messageOrder: null,
  question: t('chatsocket.completion.what_now'),
  options: [
    { name: t('chatsocket.buttons.select_plan'), type: 'MOCK_SELECT_PLAN' },
    { name: t('chatsocket.buttons.new_campaign'), type: 'MOCK_NEW_CAMPAIGN' },
  ],
  isMockOption: true,
});
